import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash.debounce";
import { queryHospitals } from "../../../http";
import { Hospital } from "../types/chatbot.types";

export const useHospitalSearch = (lat?: number, lng?: number) => {
  const [searchText, setSearchText] = useState("");
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const fetchHospitals = useCallback(async (name: string) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    try {
      const res = await queryHospitals(
        { name, limit: 10, ...(lat && lng ? { PickupLatitude: lat, PickupLongitude: lng } : {}) },
        controller
      );
      setHospitals(res?.data?.data || []);
    } catch {
      // aborted or failed
    } finally {
      setLoading(false);
    }
  }, [lat, lng]); // eslint-disable-line

  const debouncedFetch = useMemo(() => debounce(fetchHospitals, 400), [fetchHospitals]);

  useEffect(() => {
    fetchHospitals("");
    return () => {
      debouncedFetch.cancel();
      abortRef.current?.abort();
    };
  }, [fetchHospitals, debouncedFetch]); // eslint-disable-line

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    debouncedFetch(e.target.value);
  };

  return { searchText, hospitals, loading, handleChange };
};
